"use client";

import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslations } from "use-intl";
import { Card } from "@/components/ui/card";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Button } from "./button";
const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;
const DialogOverlay = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Overlay>, React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>>(({
  className,
  ...props
}, ref) => <DialogPrimitive.Overlay ref={ref} className={cn("fixed inset-0 z-50 bg-background/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0", className)} {...props} />);
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;
const DialogContent = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Content>, React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  shouldDisplayCloseButton?: boolean;
  infoContent?: React.ReactNode;
  containerClassName?: string;
  leftSideContent?: React.ReactNode;
}>(({
  className,
  children,
  shouldDisplayCloseButton = true,
  infoContent,
  containerClassName,
  leftSideContent,
  ...props
}, ref) => {
  const t = useTranslations();
  const [isInfoContentCollapsed, setIsInfoContentCollapsed] = React.useState(false);
  return <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content ref={ref} tabIndex={-1} {...props}>
          <div className={cn("fixed left-1/2 top-1/2 z-50 flex w-full max-w-lg -translate-x-1/2 -translate-y-1/2 flex-col rounded-3xl tablet:max-h-[90vh]", className)}>
            {infoContent && <Card className={`relative flex items-center justify-between overflow-hidden rounded-3xl border bg-blue-100/60 px-3 py-6 shadow-sm transition-all dark:bg-blue-700 ${isInfoContentCollapsed ? "max-h-[56px]" : "max-h-[148px]"}`}>
                <Button className="bg-tranparent absolute left-3 top-3 z-20 min-h-10 min-w-10 rounded-full p-0 text-foreground outline-none ring-0 ring-offset-0 hover:bg-card/30 hover:text-card-foreground focus:outline-none focus:ring-0" onClick={() => setIsInfoContentCollapsed(prev => !prev)} tabIndex={-1}>
                  {isInfoContentCollapsed ? <Icon icon="fluent:eye-32-filled" className="h-6 w-6" /> : <Icon icon="majesticons:eye-off-line" className="h-6 w-6" />}
                </Button>
                <div className={`absolute left-16 top-1/2 z-20 -mt-[1px] -translate-y-1/2 transform leading-normal transition-all duration-300 ${isInfoContentCollapsed ? "opacity-100" : "pointer-events-none opacity-0"}
                  `}>
                  Secțiunea cu informații este ascunsă. Apasă pentru a o afișa.
                </div>

                <Icon icon="streamline:information-desk-solid" className={`absolute left-8 h-48 w-48 -translate-y-1/2 transform text-blue-200/80 transition-all duration-500 dark:text-blue-400/30 ${isInfoContentCollapsed ? "top-20" : "top-2/3"}
                  `} />
                <div className={`scrollbar z-10 -mt-[1px] max-h-[100px] overflow-auto px-4 pl-12 leading-normal transition-all duration-300 ${isInfoContentCollapsed ? "pointer-events-none opacity-0" : "opacity-100"}`}>
                  {infoContent}
                </div>
              </Card>}

            {leftSideContent && <div>{leftSideContent}</div>}

            <div className={cn("hide-scrollbar grid min-h-0 w-full flex-1 gap-4 overflow-hidden rounded-3xl border bg-background/60 shadow-lg backdrop-blur-sm duration-200 focus:outline-none focus:ring-0 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]", className)}>
              <div className={cn("relative flex max-h-full w-full flex-col overflow-hidden focus:outline-none focus:ring-0", shouldDisplayCloseButton ? "p-6" : "", containerClassName)} tabIndex={-1}>
                <div className="scrollbar h-full max-h-full flex-1 overflow-auto">{children}</div>
                {!!shouldDisplayCloseButton && <DialogPrimitive.Close className="absolute right-2.5 top-2.5 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
                    <X className="size-4" />
                    <span className="sr-only">{t("src_components_ui_dialog_tsx_0")}</span>
                  </DialogPrimitive.Close>}
              </div>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>;
});
DialogContent.displayName = DialogPrimitive.Content.displayName;
const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => <div className={cn("flex flex-col space-y-1.5 text-center mobile:text-left", className)} {...props} data-sentry-component="DialogHeader" data-sentry-source-file="dialog.tsx" />;
DialogHeader.displayName = "DialogHeader";
const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => <div className={cn("flex flex-col-reverse mobile:flex-row mobile:justify-end mobile:space-x-2", className)} {...props} data-sentry-component="DialogFooter" data-sentry-source-file="dialog.tsx" />;
DialogFooter.displayName = "DialogFooter";
const DialogTitle = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Title>, React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>>(({
  className,
  ...props
}, ref) => <DialogPrimitive.Title ref={ref} className={cn("py-1 text-lg font-semibold leading-none tracking-tight", className)} {...props} />);
DialogTitle.displayName = DialogPrimitive.Title.displayName;
const DialogDescription = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Description>, React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>>(({
  className,
  ...props
}, ref) => <DialogPrimitive.Description ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />);
DialogDescription.displayName = DialogPrimitive.Description.displayName;
export { Dialog, DialogPortal, DialogOverlay, DialogClose, DialogTrigger, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription };